/**
 * Typed HTTP client for the FastAPI backend.
 *
 * Base URL comes from VITE_API_URL. When unset, requests are same-origin
 * (relative /v1/... paths), which pairs with the nginx SPA config that
 * reverse-proxies /v1 to the API container.
 *
 * CONTRACT NOTE (align with the backend worker): GET /v1/tenant returns the
 * resolved public TenantConfig for the requesting tenant. The backend
 * resolves the tenant from the Host header; the frontend ALSO sends the
 * resolved slug as an explicit `slug` query parameter so local dev on
 * localhost (where Host carries no tenant information) works without
 * special-casing on the server.
 */

const API_BASE_URL = (import.meta.env.VITE_API_URL ?? '').trim().replace(/\/+$/, '');

/** Base URL for API calls (empty string = same origin). Exported so callers
 * that bypass apiFetch (e.g. endpoints needing raw response bodies) stay
 * consistent with the configured backend. */
export function apiBaseUrl(): string {
  return API_BASE_URL;
}

export class ApiError extends Error {
  readonly status: number;
  readonly code: string;

  constructor(status: number, code: string, message: string) {
    super(message);
    this.name = 'ApiError';
    this.status = status;
    this.code = code;
  }

  get isNotFound(): boolean {
    return this.status === 404;
  }

  get isNetworkError(): boolean {
    return this.status === 0;
  }
}

/* ------------------------------------------------------------------ */
/* Tenant config contract                                               */
/* ------------------------------------------------------------------ */

/** Branding resolved per tenant, applied by TenantHeader at runtime. */
export interface TenantBranding {
  /** Hex color for header/buttons. Example: "#1d4ed8". */
  primary_color: string;
  /** Hex color for accents/focus rings. Defaults to primary_color. */
  accent_color?: string;
  /** Absolute or relative URL of the tenant logo. */
  logo_url?: string | null;
}

/** Static tile delivery configuration (PMTiles over CDN). */
export interface TenantMapConfig {
  /**
   * Absolute URL of the blocks PMTiles archive, e.g.
   * https://cdn.example/tiles/<hash>/blocks.pmtiles. Takes precedence when set.
   */
  tiles_url?: string;
  /**
   * Dataset hash used to build the archive URL from VITE_TILES_BASE_URL:
   * {base}/{dataset_hash}/blocks.pmtiles. Required when tiles_url is absent.
   */
  dataset_hash?: string;
  /** Vector source-layer name inside the archive, e.g. "blocks". */
  source_layer: string;
  /**
   * Stable feature property promoted to the feature id
   * (MapLibre `promoteId`), e.g. "GEOID". Drives feature-state painting.
   */
  id_property: string;
  /** [west, south, east, north] initial view bounds. */
  bounds?: [number, number, number, number];
  /** [lng, lat] fallback center when bounds are absent. */
  center?: [number, number];
  min_zoom?: number;
  max_zoom?: number;
}

/** District policy knobs resolved from tenant configuration. */
export interface TenantDistrictConfig {
  count: number;
  /** Display labels per district index, e.g. ["District 1", ...]. */
  labels?: string[];
  /** Hex fill colors per district index. */
  colors?: string[];
  /** Ideal population per district (total_population / count). */
  population_target?: number;
  /**
   * Maximum allowed |deviation| from ideal, in percent. Drives the
   * validation engine's hard failure and the stats panel's red band.
   * Matches the backend default (10) when absent.
   */
  max_population_deviation_pct?: number;
}

/** Module feature flags per tenant. */
export interface TenantFeatures {
  drawing_enabled: boolean;
  ai_sketch_enabled: boolean;
  coi_enabled: boolean;
}

/** Public tenant configuration returned by GET /v1/tenant. */
export interface TenantConfig {
  tenant_id: string;
  slug: string;
  name: string;
  tagline?: string;
  branding: TenantBranding;
  map: TenantMapConfig;
  districts: TenantDistrictConfig;
  features: TenantFeatures;
  /** Monotonic config version; used to bust cached tiles/config. */
  config_version: string;
  /**
   * Reference-layer catalog (district maps, legislative districts, census
   * geography, tribal areas). Parse with `tenantLayers()` in lib/layers.ts.
   */
  layers?: unknown;
  /**
   * Whether the jurisdiction has published adopted districts usable as a
   * plan starting point (backend derives this from the reference-layers
   * table). Absent/false means the "current districts" template is
   * unavailable.
   */
  has_adopted_districts?: boolean;
}

/* ------------------------------------------------------------------ */
/* Runtime type guards (defensive: /v1/tenant is a network boundary)    */
/* ------------------------------------------------------------------ */

function isRecord(value: unknown): value is Record<string, unknown> {
  return typeof value === 'object' && value !== null && !Array.isArray(value);
}

function isNonEmptyString(value: unknown): value is string {
  return typeof value === 'string' && value.length > 0;
}

/** Narrows `unknown` JSON to TenantConfig. Rejects malformed payloads. */
export function isTenantConfig(value: unknown): value is TenantConfig {
  if (!isRecord(value)) return false;
  if (!isNonEmptyString(value.tenant_id)) return false;
  if (!isNonEmptyString(value.slug)) return false;
  if (!isNonEmptyString(value.name)) return false;
  if (!isNonEmptyString(value.config_version)) return false;
  if (!isRecord(value.branding) || !isNonEmptyString(value.branding.primary_color)) return false;
  if (!isRecord(value.map)) return false;
  if (!isNonEmptyString(value.map.source_layer)) return false;
  if (!isNonEmptyString(value.map.id_property)) return false;
  if (!isRecord(value.districts) || typeof value.districts.count !== 'number') return false;
  if (!isRecord(value.features)) return false;
  for (const flag of ['drawing_enabled', 'ai_sketch_enabled', 'coi_enabled'] as const) {
    if (typeof value.features[flag] !== 'boolean') return false;
  }
  return true;
}

import { clearTokens, getAccessToken, getRefreshToken, setTokens } from './authToken';

/**
 * Local dev: every tenant is served from localhost, so the API can't tell
 * them apart by Host. When VITE_TENANT_OVERRIDE picks a tenant, name it in
 * an X-Tenant-Slug header (the API honors it outside production only).
 */
const DEV_TENANT_SLUG = ((import.meta.env.VITE_TENANT_OVERRIDE) ?? '').trim();

/** Headers every API call carries (dev tenant override). */
export function tenantHeaders(): Record<string, string> {
  return DEV_TENANT_SLUG ? { 'X-Tenant-Slug': DEV_TENANT_SLUG } : {};
}

/* ------------------------------------------------------------------ */
/* Fetch plumbing                                                       */
/* ------------------------------------------------------------------ */

async function parseJsonBody(response: Response): Promise<unknown> {
  const text = await response.text();
  if (!text) return undefined;
  try {
    return JSON.parse(text) as unknown;
  } catch {
    throw new ApiError(response.status, 'invalid_json', 'Backend returned a non-JSON response body.');
  }
}

/**
 * Extract the machine-readable error code from a failure body.
 *
 * Auth endpoints (and auth middleware) return
 * `{"detail": {"code": "auth_required", "message": "..."}}`; everything
 * else keeps the legacy `http_<status>` code.
 */
function extractErrorCode(body: unknown, response: Response): { code: string; message: string } {
  if (isRecord(body) && isRecord(body.detail)) {
    const code = body.detail.code;
    const message = body.detail.message;
    if (typeof code === 'string' && code.length > 0) {
      return {
        code,
        message: typeof message === 'string' && message.length > 0 ? message : response.statusText,
      };
    }
  }
  const detail = isRecord(body) && typeof body.detail === 'string' ? body.detail : response.statusText;
  return { code: `http_${response.status}`, message: detail || 'Request failed' };
}

/** True when the ApiError means "no (valid) session" — the sign-in prompt trigger. */
export function isAuthRequired(error: unknown): boolean {
  return error instanceof ApiError && error.code === 'auth_required';
}

let refreshInFlight: Promise<void> | null = null;

/**
 * Exchange the refresh token for a new pair (single-flight: concurrent
 * 401s share one refresh). Clears local tokens when the refresh itself
 * fails, so the UI falls back to the signed-out state.
 */
async function refreshAccessToken(): Promise<void> {
  if (!refreshInFlight) {
    refreshInFlight = (async () => {
      const refreshToken = getRefreshToken();
      if (!refreshToken) throw new Error('No refresh token');
      let response: Response;
      try {
        response = await fetch(`${API_BASE_URL}/v1/auth/refresh`, {
          method: 'POST',
          headers: { 'Content-Type': 'application/json', ...tenantHeaders() },
          body: JSON.stringify({ refresh_token: refreshToken }),
        });
      } catch {
        throw new Error('Refresh request failed');
      }
      if (!response.ok) {
        clearTokens();
        throw new Error('Refresh rejected');
      }
      const body = (await response.json()) as { access_token?: unknown; refresh_token?: unknown };
      if (typeof body.access_token !== 'string' || typeof body.refresh_token !== 'string') {
        clearTokens();
        throw new Error('Malformed refresh response');
      }
      setTokens(body.access_token, body.refresh_token);
    })().finally(() => {
      refreshInFlight = null;
    });
  }
  return refreshInFlight;
}

/**
 * Raw fetch with the session attached, for callers that must read non-2xx
 * bodies themselves (e.g. 422 rejection lists). Adds the access token and,
 * on `token_expired`, refreshes once and retries. Network failures become
 * ApiError(0); HTTP errors are returned, not thrown.
 */
export async function fetchWithAuth(url: string, init: RequestInit = {}, retry = true): Promise<Response> {
  const headers: Record<string, string> = {
    'Content-Type': 'application/json',
    ...tenantHeaders(),
    ...((init.headers as Record<string, string> | undefined) ?? {}),
  };
  const accessToken = getAccessToken();
  if (accessToken && !headers.Authorization) headers.Authorization = `Bearer ${accessToken}`;
  let response: Response;
  try {
    response = await fetch(url, { ...init, headers });
  } catch (error) {
    const message = error instanceof Error ? error.message : 'Network request failed';
    throw new ApiError(0, 'network_error', `Could not reach the backend: ${message}`);
  }
  if (response.status === 401 && retry && getRefreshToken()) {
    const body = await parseJsonBody(response.clone());
    if (extractErrorCode(body, response).code === 'token_expired') {
      try {
        await refreshAccessToken();
        return fetchWithAuth(url, init, false);
      } catch {
        return response;
      }
    }
  }
  return response;
}

/**
 * Low-level typed fetch. Throws ApiError on non-2xx or network failure.
 * Never throws bare fetch TypeErrors: network failures become status 0.
 *
 * Attaches the native-auth access token when one is stored. On
 * `token_expired`, refreshes once and retries the request.
 */
export async function apiFetch<T>(path: string, init?: RequestInit, retry = true): Promise<T> {
  const headers: Record<string, string> = {
    'Content-Type': 'application/json',
    ...tenantHeaders(),
    ...((init?.headers as Record<string, string> | undefined) ?? {}),
  };
  const accessToken = getAccessToken();
  if (accessToken && !headers.Authorization && !headers.authorization) {
    headers.Authorization = `Bearer ${accessToken}`;
  }

  let response: Response;
  try {
    response = await fetch(`${API_BASE_URL}${path}`, { ...init, headers });
  } catch (error) {
    const message = error instanceof Error ? error.message : 'Network request failed';
    throw new ApiError(0, 'network_error', `Could not reach the backend: ${message}`);
  }

  if (!response.ok) {
    const body = await parseJsonBody(response);
    const { code, message } = extractErrorCode(body, response);
    if (response.status === 401 && code === 'token_expired' && retry && getRefreshToken()) {
      try {
        await refreshAccessToken();
      } catch {
        throw new ApiError(response.status, code, message);
      }
      return apiFetch<T>(path, init, false);
    }
    throw new ApiError(response.status, code, message);
  }

  return (await parseJsonBody(response)) as T;
}

/**
 * Fetch the public tenant configuration.
 *
 * @param slug Tenant slug resolved client-side from the hostname. Sent as an
 * explicit query parameter so dev on localhost works; the backend may still
 * prefer the Host header when they disagree (server wins).
 */
export async function fetchTenant(slug: string): Promise<TenantConfig> {
  const payload: unknown = await apiFetch<unknown>(
    `/v1/tenant?slug=${encodeURIComponent(slug)}`,
  );
  if (!isTenantConfig(payload)) {
    throw new ApiError(502, 'invalid_tenant_config', 'Backend returned a malformed tenant config.');
  }
  return payload;
}

/** Liveness probe (GET /healthz). Resolves true when the backend is reachable. */
export async function checkBackendHealth(): Promise<boolean> {
  try {
    await apiFetch<unknown>('/healthz');
    return true;
  } catch {
    return false;
  }
}
